import { PublicClientApplication } from '@azure/msal-browser';

const DEFAULT_AUTHORITY = 'https://login.microsoftonline.com/common';
const DEFAULT_SCOPES = ['openid', 'profile', 'email'];
// Must be registered per host as an SPA redirect URI in Azure; the page broadcasts the popup response.
const REDIRECT_PATH = '/public/msal-redirect.html';
// MSAL cannot see a closed popup (COOP), so it waits this long; long enough for MFA, and nothing locks meanwhile.
const POPUP_BRIDGE_TIMEOUT_MS = 5 * 60 * 1000;

export const POPUP_BLOCKED_MESSAGE =
	'Your browser blocked the Microsoft sign-in window. Allow pop-ups for this site and try again.';
const FAILED_MESSAGE = 'Microsoft sign-in failed. Please try again.';
const MESSAGES = {
	timed_out: 'Microsoft sign-in timed out. Please try again.',
	user_cancelled: 'Microsoft sign-in was cancelled.',
	popup_window_error: POPUP_BLOCKED_MESSAGE,
	empty_window_error: POPUP_BLOCKED_MESSAGE
};
const SUPERSEDED = 'microsoft_superseded';

let pcaPromise = null;

function getPca(clientId) {
	if (pcaPromise) return pcaPromise;
	const pca = new PublicClientApplication({
		auth: {
			clientId,
			authority: DEFAULT_AUTHORITY,
			redirectUri: window.location.origin + REDIRECT_PATH
		},
		cache: { cacheLocation: 'sessionStorage' },
		system: { popupBridgeTimeout: POPUP_BRIDGE_TIMEOUT_MS }
	});
	pcaPromise = pca.initialize().then(() => pca);
	return pcaPromise;
}

/** A newer attempt took over this one's MSAL interaction; not an error to show. */
export function isMicrosoftSuperseded(err) {
	return err?.code === SUPERSEDED;
}

function normalise(err) {
	if (err?.errorCode === 'interaction_in_progress_cancelled') {
		return Object.assign(new Error('Microsoft sign-in was restarted.'), { code: SUPERSEDED });
	}
	return new Error(MESSAGES[err?.errorCode] ?? FAILED_MESSAGE);
}

/**
 * Opens the Microsoft sign-in popup and resolves with the **id_token**
 * (`result.idToken` — aud = this app's client_id; the backend verifies an
 * id_token, never `accessToken`, whose aud is Microsoft Graph and would be
 * rejected). Popup flow keeps the whole exchange on one page load.
 *
 * Every call opens a new popup: `overrideInteractionInProgress` clears an
 * interaction flag left by a reload or by an earlier, still-open popup.
 * Rejects only with fixed English messages, never MSAL's own text.
 */
export async function signInWithMicrosoft(clientId) {
	let result;
	try {
		const pca = await getPca(clientId);
		result = await pca.loginPopup({ scopes: DEFAULT_SCOPES, overrideInteractionInProgress: true });
	} catch (err) {
		throw normalise(err);
	}
	if (!result?.idToken) throw new Error(FAILED_MESSAGE);
	return result.idToken;
}
